import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  CircularProgress,
  Box,
  Button,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import InvoiceForm from './InvoiceForm';
import { API_ENDPOINTS } from '../config/api';
import { printOrDownloadPdf } from '../utils/printUtils';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Fetching invoice with ID:', id);
      const response = await axios.get(API_ENDPOINTS.invoiceById(id));
      console.log('Invoice response:', response.data);
      setInvoice(response.data);
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError('Failed to fetch invoice details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handlePrint = async () => {
    if (!invoice) return;
    
    try {
      // Fetch PDF data
      const response = await axios.post(API_ENDPOINTS.invoicePdf(invoice.id), {}, {
        responseType: 'blob'
      });
      
      // Use the utility function to print or download
      const result = await printOrDownloadPdf(
        response.data, 
        `invoice-${invoice.invoiceNumber}.pdf`,
        { showProgress: true }
      );
      
      if (result.success) {
        if (result.method === 'print') {
          console.log('✅ Invoice printed successfully');
        } else {
          console.log('📥 Invoice downloaded successfully');
        }
      } else {
        alert('Failed to print or download invoice');
      }
      
    } catch (err) {
      console.error('Failed to print invoice:', err);
      alert('Failed to print invoice: ' + (err.response?.data || err.message));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading invoice details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          color="error" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
        >
          Back to Invoices
        </Button>
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Invoice not found
        </Alert>
        <Button 
          variant="outlined" 
          color="warning" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
        >
          Back to Invoices
        </Button>
      </Box>
    );
  }

  // Pass the invoice data to InvoiceForm in edit mode
  return (
    <Box sx={{ p: 2 }}>
      {/* Print Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
        >
          Back to Invoices
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!invoice}
        >
          Print Invoice
        </Button>
      </Box>
      
      <InvoiceForm editMode={true} invoiceData={invoice} />
    </Box>
  );
};

export default InvoiceDetail;
